import { useState } from 'react';
import { SeverityBadge } from '../shared';
import { SwipeableCard, SwipeActions, ActionSheet, ActionSheetItem } from '../touch';
import { InspectionStartDialog, RestoreConfirmDialog, DeleteConfirmDialog } from '../shared';
import { useToastStore } from '../../../stores/toast';
import { isSiteAssigned, type SiteAccessMembership } from '../../../utils/siteAccess';
import { inspectionStatusConfig } from '../../../lib/inspections';
import { getInspectionStatusIcon } from '../inspectionStatusDisplay';
import { ArrowUturnLeftIcon, CheckIcon, CircleIcon, EyeIcon, TrashIcon } from '../../icons';
import { Badge, Button, Card } from '@safetyhub/ui';

interface InspectionListItem {
  id: string;
  siteName: string;
  siteId: string;
  inspectionDate: string;
  inspectorName: string;
  inspectorId: string;
  photoCount: number;
  findingCount: number;
  status: string;
  contributorCount: number;
  contributors?: { id: string; name: string }[];
  thumbnailUrl?: string;
  highestSeverity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  /** 삭제 일시 (soft delete) */
  deletedAt?: string | null;
  /** 검토된 사진 수 (완료 가능 여부 판단용) */
  reviewedPhotosCount?: number;
  /** 이전 발견사항 중 조치결과 미입력 수 (완료 가능 여부 판단용) */
  unreviewedPreviousFindingsCount?: number;
  /** 현장에서의 점검 순서 (N차) */
  sequenceNumber?: number;
  /** 우수사례 발견사항 수 */
  bestPracticeCount?: number;
  /** 해당 현장에서 가장 최근 점검 여부 */
  isLatestInspection?: boolean;
  /** 예약 점검 예정일 */
  scheduledAt?: string;
}

interface InProgressInspection {
  id: string;
  siteId: string;
  siteName: string;
  inspectorId: string;
  inspectorName: string;
  status: string;
  photoCount: number;
  findingCount: number;
  contributorCount: number;
  startedAt: string;
}

interface Site {
  id: string;
  name: string;
}

interface FilterState {
  siteId?: string;
  status?: string;
  /** 삭제된 점검 포함 여부 */
  includeDeleted?: boolean;
  /** 장소 ID 필터 */
  locationId?: string;
  /** 설비 ID 필터 */
  equipmentId?: string;
}

interface InspectionListFoldProps {
  inspections: InspectionListItem[];
  pagination: {
    currentPage: number;
    totalPages: number;
    totalCount: number;
    hasMore: boolean;
  };
  filters: FilterState;
  sites: Site[];
  inProgressInspections: InProgressInspection[];
  currentUserId: string;
  isOwner: boolean;
  // 현장 접근 판정에 쓰는 조각만(`isSiteAssigned`). 옛 `role`은 여기서 읽지 않는다 — 소유자
  // 성질은 `isOwner` 필드가 유일한 원천이다(`utils/siteAccess` doc).
  currentMembership?: SiteAccessMembership | null;
  selectedSiteId?: string;
  isLoading?: boolean;
  onFilterChange: (filters: FilterState) => void;
  onLoadMore: () => void;
  onViewInspection: (id: string) => void;
  onDeleteInspection: (id: string) => Promise<{ success: boolean; error?: string } | void>;
  onRestoreInspection?: (id: string) => Promise<{ success: boolean; error?: string }>;
  onStartNewClick: () => void;
  onStartNew: (siteId: string) => void;
  onContinueInspection: (inspectionId: string) => void;
  /** 삭제된 점검 포함 토글 */
  onToggleIncludeDeleted?: (include: boolean) => void;
  /** 예약 점검 시작 */
  onStartScheduled?: (id: string) => void;
}

const statusConfig = inspectionStatusConfig;

const defaultStatus = {
  label: '알 수 없음',
  color: 'text-ink-600 bg-surface-soft',
  tone: 'slate' as const,
};

export function InspectionListFold({
  inspections,
  pagination,
  filters,
  sites,
  inProgressInspections,
  currentUserId,
  isOwner,
  currentMembership,
  selectedSiteId,
  isLoading = false,
  onFilterChange,
  onLoadMore,
  onViewInspection,
  onDeleteInspection,
  onRestoreInspection,
  onStartNewClick,
  onStartNew,
  onContinueInspection,
  onToggleIncludeDeleted,
}: InspectionListFoldProps) {
  const { showToast } = useToastStore();
  const [showStartDialog, setShowStartDialog] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<InspectionListItem | null>(null);
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  // 삭제된 점검 복원 확인 다이얼로그
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [deletedInspectionForRestore, setDeletedInspectionForRestore] = useState<InspectionListItem | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);
  // 점검 삭제 확인 다이얼로그
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [inspectionToDelete, setInspectionToDelete] = useState<InspectionListItem | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleStartNewClick = () => {
    if (inProgressInspections.length > 0 && selectedSiteId) {
      setShowStartDialog(true);
    } else {
      onStartNewClick();
    }
  };

  const siteInProgressInspections = selectedSiteId
    ? inProgressInspections.filter((i) => i.siteId === selectedSiteId)
    : [];

  const selectedSite = sites.find((s) => s.id === selectedSiteId);

  // 점검 클릭 핸들러 - 삭제 여부에 따라 분기
  const handleInspectionClick = (inspection: InspectionListItem) => {
    if (inspection.deletedAt) {
      // 삭제된 점검: 복원 확인 다이얼로그 표시
      setDeletedInspectionForRestore(inspection);
      setShowRestoreDialog(true);
    } else {
      // 활성 점검: 상세 페이지로 이동
      onViewInspection(inspection.id);
    }
  };

  // 복원 처리
  const handleRestore = async () => {
    if (!deletedInspectionForRestore || !onRestoreInspection) return;

    setIsRestoring(true);
    try {
      const result = await onRestoreInspection(deletedInspectionForRestore.id);
      setShowRestoreDialog(false);
      setDeletedInspectionForRestore(null);

      if (result.success) {
        showToast('점검이 복원되었습니다.', 'success');
      } else {
        showToast(result.error || '복원에 실패했습니다.', 'error');
      }
    } finally {
      setIsRestoring(false);
    }
  };

  // 복원 권한 확인
  const canRestoreInspection = (inspection: InspectionListItem) => {
    return isOwner || inspection.inspectorId === currentUserId;
  };

  // 삭제 버튼 클릭 - 확인 다이얼로그 표시
  const handleDeleteClick = (id: string) => {
    const inspection = inspections.find((i) => i.id === id);
    if (inspection) {
      setInspectionToDelete(inspection);
      setShowDeleteDialog(true);
    }
  };

  // 삭제 확인 처리
  const handleDeleteConfirm = async () => {
    if (!inspectionToDelete) return;

    setIsDeleting(true);
    try {
      const result = await onDeleteInspection(inspectionToDelete.id);
      // 성공 시에만 다이얼로그 닫기
      if (result?.success) {
        setShowDeleteDialog(false);
        setInspectionToDelete(null);
      }
      // 실패 시 다이얼로그 유지 (토스트는 hook에서 표시)
    } finally {
      setIsDeleting(false);
    }
  };

  const getActionItems = (): ActionSheetItem[] => {
    if (!selectedInspection) return [];

    // 삭제/복원 권한 체크: 조직 소유자이거나 점검 생성자이고 현장 담당자인 경우에만 가능
    const canModify = (isOwner || selectedInspection.inspectorId === currentUserId) && isSiteAssigned(currentMembership ?? null, selectedInspection.siteId);
    const isDeleted = !!selectedInspection.deletedAt;
    // 이후 차수 점검이 존재하면 삭제 불가
    const isLockedByLaterInspection = selectedInspection.isLatestInspection === false;

    const items: ActionSheetItem[] = [
      {
        id: 'view',
        label: '상세 보기',
        icon: EyeIcon,
        onClick: () => onViewInspection(selectedInspection.id),
      },
    ];

    if (isDeleted && onRestoreInspection) {
      // 삭제된 점검: 복원 옵션
      items.push({
        id: 'restore',
        label: '복원',
        icon: ArrowUturnLeftIcon,
        onClick: () => onRestoreInspection(selectedInspection.id),
        danger: false,
        disabled: !canModify,
        disabledReason: !canModify ? '점검 생성자 또는 조직 관리자만 복원할 수 있습니다.' : undefined,
      });
    } else {
      // 활성 점검: 삭제 옵션
      const isDeleteDisabled = !canModify || isLockedByLaterInspection;
      let deleteDisabledReason: string | undefined;
      if (isLockedByLaterInspection) {
        deleteDisabledReason = '이후 차수의 점검이 존재하여 삭제할 수 없습니다.';
      } else if (!canModify) {
        deleteDisabledReason = '점검 생성자 또는 조직 관리자만 삭제할 수 있습니다.';
      }
      items.push({
        id: 'delete',
        label: '삭제',
        icon: TrashIcon,
        onClick: () => handleDeleteClick(selectedInspection.id),
        danger: canModify && !isLockedByLaterInspection,
        disabled: isDeleteDisabled,
        disabledReason: deleteDisabledReason,
      });
    }

    return items;
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* 헤더 */}
      <header className="bg-white border-b border-line sticky top-14 z-40">
        <div className="px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            {/* 굵기·자간은 좁은 화면 헤더 공통 결. 크기만 폴드 폭에 맞춰 한 단 크다. */}
            <h1 className="text-xl font-bold tracking-tight text-ink-900">점검 목록</h1>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleStartNewClick}
            >
              <span>+</span>
              새 점검
            </Button>
          </div>

          {/* 필터 */}
          <div className="flex gap-2 overflow-x-auto pb-1">
            <select
              value={filters.siteId || ''}
              onChange={(e) =>
                onFilterChange({ ...filters, siteId: e.target.value || undefined })
              }
              className="px-3 py-1.5 border border-ink-300 rounded-control text-sm bg-white"
            >
              <option value="">모든 현장</option>
              {sites.map((site) => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))}
            </select>

            <select
              value={filters.status || ''}
              onChange={(e) =>
                onFilterChange({
                  ...filters,
                  status: e.target.value || undefined,
                })
              }
              className="px-3 py-1.5 border border-ink-300 rounded-control text-sm bg-white"
            >
              <option value="">모든 상태</option>
              <option value="SCHEDULED">예약됨</option>
              <option value="IN_PROGRESS">진행중</option>
              <option value="PENDING_REVIEW">검토 대기</option>
              <option value="COMPLETED">완료</option>
            </select>

            {/* 삭제된 점검 보기 */}
            {onToggleIncludeDeleted && (
              <label className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-ink-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={filters.includeDeleted || false}
                  onChange={(e) => onToggleIncludeDeleted(e.target.checked)}
                  className="w-4 h-4 text-brand-600 border-ink-300 rounded"
                />
                삭제됨
              </label>
            )}

            <span className="px-3 py-1.5 text-sm text-ink-500 whitespace-nowrap">
              총 {pagination.totalCount}건
            </span>
          </div>
        </div>
      </header>

      {/* 목록 */}
      <main className="p-4">
        {inspections.length === 0 ? (
          <Card padding="none" radius="lg" className="p-8 text-center">
            <p className="text-ink-500 mb-3">점검 내역이 없습니다</p>
            <button
              type="button"
              onClick={handleStartNewClick}
              className="text-brand-600 font-medium"
            >
              + 새 점검 시작
            </button>
          </Card>
        ) : (
          <div className="space-y-3">
            {inspections.map((inspection) => {
              const status = statusConfig[inspection.status] || defaultStatus;
              // 삭제 권한 체크: 조직 소유자이거나 점검 생성자이고 현장 담당자인 경우에만 삭제 가능
              const canDelete = (isOwner || inspection.inspectorId === currentUserId) && isSiteAssigned(currentMembership ?? null, inspection.siteId);
              const isDeleted = !!inspection.deletedAt;
              // 이후 차수 점검이 존재하면 스와이프 삭제도 비활성화
              const isLockedByLaterInspection = inspection.isLatestInspection === false;

              return (
                <SwipeableCard
                  key={inspection.id}
                  rightActions={canDelete && !isDeleted && !isLockedByLaterInspection ? [SwipeActions.delete(() => handleDeleteClick(inspection.id))] : []}
                >
                  <div
                    className={`bg-white border rounded-card p-4 ${isDeleted ? 'opacity-60 bg-surface-soft' : ''}`}
                    onClick={() => handleInspectionClick(inspection)}
                  >
                    <div className="flex gap-3">
                      {/* 썸네일 */}
                      <div className="w-16 h-16 flex-shrink-0 rounded-card-sm bg-ink-100 overflow-hidden">
                        {inspection.thumbnailUrl ? (
                          <img
                            src={inspection.thumbnailUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-ink-400">
                          </div>
                        )}
                      </div>

                      {/* 정보 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-1">
                          <h3 className="font-medium text-ink-900 truncate">
                            {inspection.siteName} {inspection.sequenceNumber ?? 1}차 점검
                          </h3>
                          {/* 삭제된 점검은 액션 메뉴 숨김 */}
                          {!isDeleted && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedInspection(inspection);
                                setIsActionSheetOpen(true);
                              }}
                              className="text-ink-400 p-1 -mr-1"
                            >
                              ⋯
                            </button>
                          )}
                        </div>

                        <p className="text-sm text-ink-500 mb-2">
                          {formatDate(inspection.inspectionDate)} · {inspection.inspectorName}
                        </p>

                        <div className="flex items-center gap-3 flex-wrap">
                          {/* 완료 가능 여부에 따른 상태 표시 */}
                          {(() => {
                            if (inspection.status === 'SCHEDULED') {
                              return (
                                <Badge tone="violet">
                                  <CircleIcon className="h-3.5 w-3.5" />
                                  예약됨
                                </Badge>
                              );
                            }
                            if (inspection.status === 'IN_PROGRESS' && inspection.reviewedPhotosCount !== undefined) {
                              const totalPhotos = inspection.photoCount;
                              const reviewedPhotos = inspection.reviewedPhotosCount;
                              const allPhotosReviewed = totalPhotos > 0 && reviewedPhotos === totalPhotos;
                              const canComplete = totalPhotos > 0 && allPhotosReviewed;

                              if (canComplete) {
                                return (
                                  <Badge tone="emerald">
                                    <CheckIcon className="h-3.5 w-3.5" />
                                    완료 가능
                                  </Badge>
                                );
                              }
                            }
                            const StatusIcon = getInspectionStatusIcon(inspection.status);
                            return (
                              <Badge tone={status.tone}>
                                <StatusIcon className="h-3.5 w-3.5" />
                                {status.label}
                              </Badge>
                            );
                          })()}

                          {isDeleted && (
                            <Badge tone="rose" shape="rounded">
                              삭제됨
                            </Badge>
                          )}

                          {inspection.highestSeverity && inspection.highestSeverity !== 'LOW' && (
                            <SeverityBadge severity={inspection.highestSeverity} size="sm" />
                          )}

                          <span className="text-xs text-ink-500">
                            {inspection.photoCount}장 · ⚠ {inspection.findingCount}건
                            {inspection.bestPracticeCount != null && inspection.bestPracticeCount > 0 && (
                              <span className="text-emerald-600 ml-1">(모범 {inspection.bestPracticeCount})</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </SwipeableCard>
              );
            })}

            {/* 더 보기 */}
            {pagination.hasMore && (
              <button
                type="button"
                onClick={onLoadMore}
                disabled={isLoading}
                className="w-full py-3 text-brand-600 font-medium bg-white border rounded-control"
              >
                {isLoading ? '로딩 중...' : '더 보기'}
              </button>
            )}
          </div>
        )}
      </main>

      {/* 액션 시트 */}
      <ActionSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        items={getActionItems()}
      />

      {/* 점검 시작 다이얼로그 */}
      <InspectionStartDialog
        isOpen={showStartDialog}
        onClose={() => setShowStartDialog(false)}
        currentUserId={currentUserId}
        siteName={selectedSite?.name || ''}
        inProgressInspections={siteInProgressInspections}
        onStartNew={() => {
          if (selectedSiteId) {
            onStartNew(selectedSiteId);
          }
          setShowStartDialog(false);
        }}
        onContinue={(inspectionId) => {
          onContinueInspection(inspectionId);
          setShowStartDialog(false);
        }}
        isLoading={isLoading}
      />

      {/* 삭제된 점검 복원 확인 다이얼로그 */}
      <RestoreConfirmDialog
        isOpen={showRestoreDialog}
        onClose={() => {
          setShowRestoreDialog(false);
          setDeletedInspectionForRestore(null);
        }}
        onRestore={handleRestore}
        inspection={deletedInspectionForRestore ? {
          siteName: deletedInspectionForRestore.siteName,
          inspectionDate: deletedInspectionForRestore.inspectionDate,
          inspectorName: deletedInspectionForRestore.inspectorName,
        } : null}
        isLoading={isRestoring}
        canRestore={deletedInspectionForRestore ? canRestoreInspection(deletedInspectionForRestore) : false}
      />

      {/* 점검 삭제 확인 다이얼로그 */}
      <DeleteConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setInspectionToDelete(null);
        }}
        onDelete={handleDeleteConfirm}
        inspection={inspectionToDelete ? {
          siteName: inspectionToDelete.siteName,
          inspectionDate: inspectionToDelete.inspectionDate,
          inspectorName: inspectionToDelete.inspectorName,
        } : null}
        isLoading={isDeleting}
      />
    </div>
  );
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    month: 'short',
    day: 'numeric',
  });
}

export default InspectionListFold;
